import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import {
    GetClipEndTime,
    GetFootageUrlFromClip,
    GetSetTimeFromClip,
    GetClipCurrentTime,
    GetCurrentClipIndexFromTime,
} from "../../../utils/TimelineUtils";
import {
    PlayTimeline,
    PauseTimeline,
    FinishTimeline,
    UpdateTimelinePlayInfo,
    UpdateTimelineSourceIndex,
    ReplayTimeline
} from "../../store/actions/TimelineActions";
import styles from "./TimelineVideoPlayer.module.css";
import PlayIcon from "../../../assets/image/play02.svg";
import RewindIcon from "../../../assets/image/fastrewind01.svg";
import FastForwardIcon from "../../../assets/image/fastrewind02.svg";
import PauseIcon from "../../../assets/image/pause.svg";
import TimelinePrograssBar from "./TimelinePrograssBar";
import { CircularProgress, makeStyles, Slider, createStyles } from "@material-ui/core";
import TimelineBar from "./TimelineBar";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";

const useStyles = makeStyles(() =>
    createStyles({
        slider: {
            "& .MuiSlider-track": {
                color: "var(--white)",
            },
            "& .MuiSlider-thumb": {
                color: "var(--white)",
            },
        },
    })
);

export const TimelineVideoPlayer = () => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const {
        clipList,
        sourceIndex,
        sourceLength,
        clipTimes,
        currentTime,
        totalTime,
        isFinished,
        isPlay,
        isClipMove,
        isAddClipToTimelineComplete,
        isSuccessDeleteClip,
        isHandleKeyboard,
    } = useSelector((state: AppState) => ({
        clipList: state.timeline.timeline?.clipList,
        sourceIndex: state.timeline.timelinePlayInfo.sourceIndex,
        sourceLength: state.timeline.timelinePlayInfo.sourceLength,
        clipTimes: state.timeline.timelinePlayInfo.clipTimes,
        currentTime: state.timeline.timelinePlayInfo.currentTime,
        totalTime: state.timeline.timelinePlayInfo.totalTime,
        isFinished: state.timeline.timelinePlayInfo.isFinished,
        isPlay: state.timeline.timelinePlayInfo.isPlay,
        isClipMove: state.timeline.isClipMove,
        isAddClipToTimelineComplete: state.timeline.isAddClipToTimelineComplete,
        isSuccessDeleteClip: state.timeline.isSuccessDeleteClip,
        isHandleKeyboard: state.timeline.timelinePlayInfo.isHandleKeyboard
    }));

    const videoRefs = useRef<HTMLVideoElement[]>([]);

    const [isVideoLoading, setIsVideoLoading] = useState<boolean>(false);
    // ミュートの判断
    const [isMute, setIsMute] = useState<boolean>(false);
    // ボリューム
    const [volume, setVolume] = useState<number>(1);

    if (!videoRefs.current) videoRefs.current = [];
    const addToRefs = (el: HTMLVideoElement) => {
        if (!el) return;
        if (el && !videoRefs.current.includes(el)) {
            videoRefs.current.push(el);
        }
    };

    const handleVideoOnLoadStart = () => {
        setIsVideoLoading(true);
    };

    const handleVideoOnLoad = () => {
        setIsVideoLoading(false);
    };

    // プレイ、一時停止
    const handlePlayPauseVideo = () => {
        if (!clipList) return;
        const currentIndex = GetCurrentClipIndexFromTime(currentTime, clipTimes, sourceLength);
        let video = videoRefs.current[currentIndex];

        if (!isPlay) {
            if (isFinished) {
                // リプレイするためのリセット
                handleReset();
                dispatch(ReplayTimeline());
                video = videoRefs.current[0];
            }

            // プレイ
            handlePlay(video);
        } else {
            // 一時停止
            handlePause();
        }
    };

    // 再生
    const handlePlay = (video: HTMLVideoElement) => {
        video.play();
        dispatch(PlayTimeline());
    };

    // 再生の一時停止
    const handlePause = (video?: HTMLVideoElement, index?: number) => {
        if (!isPlay) return;

        if (video && index) {
            // 指定クリップの一時停止
            video.pause();
        } else {
            // 指定クリップがない場合、全体の一時停止
            for (let i = 0; i < sourceLength; i++) {
                const video = videoRefs.current[i];
                video.pause();
            }
        }
        dispatch(PauseTimeline());
    };

    // 再生のリセット
    const handleReset = () => {
        if (!clipList || !videoRefs) return;
        for (let i = 0; i < sourceLength; i++) {
            const video = videoRefs.current[i];
            video.currentTime = clipList[i].start / 1000;
        }
    };

    // 「←」キーで早戻り
    const handleFastRewindVideo = () => {
        if (currentTime <= 0 || !clipList) return;

        // 1秒ずつ戻る
        const rewindTime = currentTime - 1000;
        const currentClipIndex = GetCurrentClipIndexFromTime(rewindTime, clipTimes, sourceLength);
        const clipCurrentTime = GetClipCurrentTime(rewindTime, clipTimes);
        const video = videoRefs.current[currentClipIndex];
        const videoStartTime = clipList[currentClipIndex].start;
        if (video) video.currentTime = Math.round((videoStartTime + clipCurrentTime) / 1000);

        if (sourceIndex !== currentClipIndex) dispatch(UpdateTimelineSourceIndex(currentClipIndex));

        dispatch(UpdateTimelinePlayInfo(rewindTime, null, null, null));
    };

    // 「→」キーで早送り
    const handleFastForwardVideo = () => {
        if (currentTime >= totalTime || !clipList) return;

        // 1秒ずつ増やす
        const forwardTime = currentTime + 1000;
        const currentClipIndex = GetCurrentClipIndexFromTime(forwardTime, clipTimes, sourceLength);
        const clipCurrentTime = GetClipCurrentTime(forwardTime, clipTimes);
        const video = videoRefs.current[currentClipIndex];
        const videoStartTime = clipList[currentClipIndex].start;
        if (video) video.currentTime = Math.round((videoStartTime + clipCurrentTime) / 1000);

        if (sourceIndex !== currentClipIndex) dispatch(UpdateTimelineSourceIndex(currentClipIndex));

        dispatch(UpdateTimelinePlayInfo(currentTime + 1000, null, null, null));
    };

    // キーボードによる操作
    const handleKeyboardEvent = (event: KeyboardEvent) => {
        // モーダルを編集する際に、キーボードによるvideoのコントロールを無効にする
        if (!isHandleKeyboard || !clipList || clipList.length === 0) return;
        // console.log("event.keyCode: ", event.keyCode);

        switch (String(event.keyCode)) {
            // 「Spaceキー」で動画の再生、一時停止
            case "32":
                event.preventDefault();
                handlePlayPauseVideo();
                break;

            // 「←」キーで早戻り
            case "37":
                event.preventDefault();
                handleFastRewindVideo();
                break;

            // 「→」キーで早送り
            case "39":
                event.preventDefault();
                handleFastForwardVideo();
                break;

                // 「V」キーでクリップの選択
                // case "86":
                // event.preventDefault();
                // console.log("選択");
                // break;

                // 「✖️」キーで削除
                // case "8":
                // event.preventDefault();
                // console.log("削除");
                // break;

            default:
                break;
        }
    };

    // タイムライン再生中のコントロール
    useEffect(() => {
        if (!isPlay || !clipList) {
            // 一時停止
            handlePause();
            return;
        }

        const timer = setInterval(() => {
            const timelineCurrentTime = currentTime + 1000;

            // 1秒ずつ増やす
            dispatch(UpdateTimelinePlayInfo(timelineCurrentTime, null, null, null));

            // 次のクリップを再生
            const timelineClipEndTime = GetClipEndTime(sourceIndex, clipTimes);
            if (
                isPlay &&
                sourceIndex < (sourceLength - 1) &&
                timelineCurrentTime === timelineClipEndTime
            ) {
                const nextSourceIndex = sourceIndex + 1;
                const currentVideo = videoRefs.current[sourceIndex];
                const nextVideo = videoRefs.current[nextSourceIndex];
                dispatch(UpdateTimelineSourceIndex(nextSourceIndex));

                currentVideo.pause();
                nextVideo.currentTime = clipList[nextSourceIndex].start / 1000;
                nextVideo.play();
            }

            // 再生終了
            if (timelineCurrentTime >= totalTime) {
                dispatch(FinishTimeline());
                handlePause();
            }
        }, 1000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlay, currentTime]);

    // キーボード操作
    useEffect(() => {
        if (videoRefs.current.length === 0) return;
        document.addEventListener("keydown", handleKeyboardEvent, false);
        return () => {
            // クリーンアップ
            document.removeEventListener("keydown", handleKeyboardEvent, false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isPlay, currentTime, videoRefs.current.length, isHandleKeyboard]);

    useEffect(() => {
        // クリップの移動、追加、編集した後、videoをリロードさせる
        if (!clipList || !videoRefs) return;

        for (let i = 0; i < sourceLength; i++) {
            const video = videoRefs.current[i];
            video?.load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClipMove, isAddClipToTimelineComplete, isSuccessDeleteClip]);

    useEffect(() => {
        for (let i = 0; i < sourceLength; i++) {
            const video = videoRefs.current[i];
            video?.load();
        }

        // 再生中にカットした場合、そのまま再生する
        if (isPlay && currentTime && clipTimes && clipList) {
            const clipCurrentTime = GetClipCurrentTime(currentTime, clipTimes);
            const videoStartTime = clipList[sourceIndex].start;
            videoRefs.current[sourceIndex].currentTime = Math.round((videoStartTime + clipCurrentTime) / 1000);
            videoRefs.current[sourceIndex].play();
        }

        if (!clipList || clipList.length === 0) {
            setIsVideoLoading(false);
            videoRefs.current = [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clipList]);

    // 動画全体を消音する
    useEffect(() => {
        for (let i = 0; i < sourceLength; i++) {
            const video = videoRefs.current[i];
            video.muted = isMute;
        }
        // eslint-disable-next-line
    }, [clipList, isMute]);

    // 動画全体のボリュームを変更する
    useEffect(() => {
        for (let i = 0; i < sourceLength; i++) {
            const video = videoRefs.current[i];
            video.volume = Number(volume);
        }
        // eslint-disable-next-line
    }, [clipList, volume]);

    // 音声アイコン押下時
    const handleMuteIcon = (mute: boolean) => {
        setIsMute(mute);
    };

    // ボリュームスライダー変更時
    const VolumeFuction = (event: React.ChangeEvent<{}>, value: number | number[]) => {
        setVolume(Number(value));
    };

    return (
        <>
            <div
                className={`${styles.videoArea} ${
                    !clipList || clipList.length === 0 ? styles.isBtnDisabled : ""
                }`}
            >
                <div className={styles.videos}>
                    <>
                        {clipList &&
                            clipList.length > 0 &&
                            clipList.map((clip, index) => (
                                <video
                                    key={index}
                                    ref={addToRefs}
                                    // muted
                                    // controls
                                    controlsList="nofullscreen nodownload noremoteplayback"
                                    disablePictureInPicture
                                    data-index={index}
                                    data-id={clip.id}
                                    data-start={clip.start}
                                    data-end={clip.end}
                                    data-seq={clip.seq}
                                    // data-id={props.clipId}
                                    // data-start={start}
                                    // data-end={end}
                                    // preload="metadata"
                                    // onPause={handleOnPause}
                                    onLoadStart={handleVideoOnLoadStart}
                                    onLoadedMetadata={handleVideoOnLoad}
                                    style={{
                                        zIndex: sourceIndex === index ? 1 : 0,
                                        opacity: sourceIndex === index ? 1 : 0,
                                    }}
                                >
                                    <source
                                        src={`${GetFootageUrlFromClip(clip)}${GetSetTimeFromClip(
                                            clip
                                        )}`}
                                        type="video/mp4"
                                    />
                                </video>
                            ))}

                        {isVideoLoading && clipList && (
                            <div className={styles.loading}>
                                <CircularProgress
                                    size={30}
                                    style={{ color: "var(--loading-bg)" }}
                                />
                            </div>
                        )}
                        {isMute ? (
                            <div className={styles.volume} onClick={() => handleMuteIcon(false)}>
                                <VolumeOffIcon />
                            </div>
                        ) : (
                            <div className={styles.volume} onClick={() => handleMuteIcon(true)}>
                                <VolumeUpIcon />
                            </div>
                        )}
                        {!isMute && (
                            <div className={styles.volumeSlider}>
                                <Slider
                                    aria-label="Volume"
                                    value={volume}
                                    onChange={VolumeFuction}
                                    max={1}
                                    step={0.1}
                                    className={classes.slider}
                                />
                            </div>
                        )}
                    </>
                </div>

                {/* 動画のコントローラーエリア */}
                <div className={styles.controlArea}>
                    <div className={styles.playArea}>
                        <img
                            src={RewindIcon}
                            alt="rewindIcon"
                            onClick={handleFastRewindVideo}
                            className={styles.playAreaIcon}
                        />
                        {isPlay ? (
                            <img
                                src={PauseIcon}
                                alt="pauseIcon"
                                onClick={handlePlayPauseVideo}
                                className={`${styles.playAreaIcon} ${styles.pauseIcon}`}
                            />
                        ) : (
                            <img
                                src={PlayIcon}
                                alt="playIcon"
                                onClick={handlePlayPauseVideo}
                                className={`${styles.playAreaIcon} ${styles.playIcon}`}
                            />
                        )}
                        <img
                            src={FastForwardIcon}
                            alt="FastForwardIcon"
                            onClick={handleFastForwardVideo}
                            className={styles.playAreaIcon}
                        />
                    </div>

                    <TimelinePrograssBar
                        videoRefs={videoRefs}
                        handleReset={handleReset}
                    />
                </div>
            </div>

            <TimelineBar
                handleReset={handleReset}
                videoRefs={videoRefs}
                sourceIndex={sourceIndex}
            />
        </>
    );
};
